import { useState, useEffect, useMemo } from "react";

const HIDDEN_KEY_PREFIX = "tableColumnsHidden_";
const COLLAPSED_KEY_PREFIX = "tableGroupsCollapsed_";

const isPersistableId = (tableId) =>
  tableId &&
  !["", "undefined", "null"].includes(tableId) &&
  typeof tableId === "string";

const getGroupParents = (columns) =>
  columns.filter((col) => col?.group?.children?.length);

export const useTableColumns = (tableId, initialColumns) => {
  const getInitialHiddenColumns = () => {
    const savedHiddenIds = localStorage.getItem(
      `${HIDDEN_KEY_PREFIX}${tableId}`,
    );

    if (savedHiddenIds) {
      try {
        const parsedIds = JSON.parse(savedHiddenIds);

        if (Array.isArray(parsedIds)) {
          return parsedIds;
        }
      } catch (error) {
        console.error("Failed to parse saved hidden columns:", error);
      }
    }

    // No saved preferences = all columns visible
    return [];
  };

  const [hiddenColumnIds, setHiddenColumnIds] = useState(
    getInitialHiddenColumns,
  );

  const getInitialCollapsedGroups = () => {
    const saved = localStorage.getItem(`${COLLAPSED_KEY_PREFIX}${tableId}`);

    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) return parsed;
      } catch (error) {
        console.error("Failed to parse saved collapsed groups:", error);
      }
    }

    return getGroupParents(initialColumns)
      .filter((col) => col.group.defaultCollapsed)
      .map((col) => col.id);
  };

  const [collapsedGroups, setCollapsedGroups] = useState(
    getInitialCollapsedGroups,
  );

  useEffect(() => {
    if (isPersistableId(tableId)) {
      localStorage.setItem(
        `${COLLAPSED_KEY_PREFIX}${tableId}`,
        JSON.stringify(collapsedGroups),
      );
    }
  }, [collapsedGroups, tableId]);

  useEffect(() => {
    const currentColumnIds = initialColumns.map((col) => col.id);
    setHiddenColumnIds((prev) =>
      prev.filter((id) => currentColumnIds.includes(id)),
    );
  }, [initialColumns]);

  useEffect(() => {
    if (
      tableId &&
      !["", "undefined", "null"].includes(tableId) &&
      typeof tableId === "string"
    ) {
      localStorage.setItem(
        `${HIDDEN_KEY_PREFIX}${tableId}`,
        JSON.stringify(hiddenColumnIds),
      );
    }
  }, [hiddenColumnIds, tableId]);

  const childToParent = {};
  getGroupParents(initialColumns).forEach((parent) => {
    parent.group.children.forEach((childId) => {
      childToParent[childId] = parent.id;
    });
  });

  const filteredCols = useMemo(() => initialColumns.map((col) => {
    const parentId = childToParent[col.id];
    const isParent = Boolean(col?.group?.children?.length);

    const parentVisible =
      parentId === undefined || !hiddenColumnIds.includes(parentId);

    return {
      ...col,
      checked: !hiddenColumnIds.includes(col.id),
      groupParentId: parentId,
      isGroupParent: isParent,
      groupCollapsed: isParent ? collapsedGroups.includes(col.id) : undefined,
      collapsed:
        parentId !== undefined &&
        parentVisible &&
        collapsedGroups.includes(parentId),
    };
  }),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [initialColumns, hiddenColumnIds, collapsedGroups]);

  const handleGroupToggle = (parentId) => {
    setCollapsedGroups((prev) =>
      prev.includes(parentId)
        ? prev.filter((id) => id !== parentId)
        : [...prev, parentId],
    );
  };

  const handleColChecked = (id, checked) => {
    setHiddenColumnIds((prevIds) =>
      checked ? prevIds.filter((colId) => colId !== id) : [...prevIds, id],
    );
  };

  const handleShowAllCols = (show) => {
    setHiddenColumnIds(show ? [] : initialColumns.map((col) => col.id));
  };

  return {
    filteredCols,
    handleColChecked,
    handleShowAllCols,
    handleGroupToggle,
  };
};
